import { Icon } from "components/Icon";
import { isNil, isNumber, range, times } from "lodash";
import { Form, Pagination, Spinner } from "react-bootstrap";
import { Button } from "components/Button";
import { useState } from "react";
import classNames from "classnames";
import useMediaQuery from "components/utils/useMediaQuery";

import "./DataGridPaging.scss";

export const DataGridPaging: React.FC<{
    className?: string;
    style?: React.CSSProperties;
    pageNumber: number;
    pageSize?: number;
    totalRecords?: number;
    pagesCount: number;
    isLoading?: boolean;
    onPageChange: (pageNumber: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
}> = ({ className, style, pageNumber, pageSize = 10, pagesCount, isLoading, totalRecords = 0, onPageChange, onPageSizeChange }) => {
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [jumpPage, setJumpPage] = useState<number>();
    const existingPages = range(1, pagesCount + 1);

    if (totalRecords < 10) {
        return null;
    }

    const activePageTitle = (title: number) =>
        isLoading ? (
            <Spinner animation="border" size="sm" role="status">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
        ) : (
            title
        );

    const pageOffset = isMobile ? 1 : 2;
    const shownNumberOfPages = isMobile ? 3 : 5;
    const showPrevNext = pagesCount > shownNumberOfPages;
    const isActivePage = (page: number) => page === pageNumber;
    const pageDoesntExist = !isNil(jumpPage) && !existingPages.find((ep) => ep === jumpPage);

    let firstPageNumber = Math.max(1, pageNumber - pageOffset);
    if (shownNumberOfPages >= pagesCount) {
        firstPageNumber = 1;
    }
    if (shownNumberOfPages < pagesCount && pageNumber + pageOffset > pagesCount) {
        firstPageNumber = pagesCount - pageOffset * 2;
    }

    let pageItemsCount = 0;
    if (pagesCount < shownNumberOfPages) {
        pageItemsCount = pagesCount;
    } else if (pagesCount > shownNumberOfPages && firstPageNumber + shownNumberOfPages > pagesCount) {
        pageItemsCount = pagesCount - firstPageNumber + 1;
    } else {
        pageItemsCount = shownNumberOfPages;
    }

    return (
        <div
            style={style}
            className={classNames("justify-content-between data-grid-paging d-flex flex-row flex-shrink-0", className, {
                "gap-2": !isMobile,
            })}
        >
            <div
                className={classNames("d-flex align-items-center justify-content-center", {
                    "flex-column": isMobile,
                    "gap-3": !isMobile,
                })}
            >
                {!isMobile && onPageSizeChange && (
                    <>
                        <span>Items per page:</span>
                        <Form.Select
                            value={pageSize}
                            name="pageSize"
                            aria-label="Change Page Size"
                            className="bg-white page-select w-auto"
                            onChange={(e) => {
                                onPageChange(1);
                                onPageSizeChange(Number(e.target.value));
                            }}
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                        </Form.Select>
                    </>
                )}
                <span
                    className={classNames("text-nowrap", {
                        "fs-6": isMobile,
                    })}
                >
                    {(pageNumber - 1) * pageSize + 1} - {Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords}
                </span>
            </div>
            <div className="gap-3 d-flex justify-content-center align-items-center">
                <nav role="navigation" aria-label="Pagination Navigation" className="my-auto">
                    <Pagination {...(isMobile ? { size: "lg" } : {})} className="my-auto">
                        {showPrevNext && (
                            <Pagination.Item
                                linkClassName="py-1 px-3"
                                aria-label={pageNumber === 1 ? undefined : `Goto Previous Page, Page ${pageNumber - 1}`}
                                disabled={pageNumber === 1}
                                onClick={() => onPageChange(pageNumber - 1)}
                                data-metadata={JSON.stringify({ name: `Goto Previous Page, Page ${pageNumber - 1}` })}
                            >
                                <Icon icon="chevron-left" style={{ height: 13 }} />
                            </Pagination.Item>
                        )}
                        {times(pageItemsCount, (index) => (
                            <Pagination.Item
                                key={index}
                                linkClassName="py-1 px-3"
                                className={classNames("", {
                                    "wide-title": (isActivePage(firstPageNumber + index) && isLoading) || firstPageNumber + index > 9,
                                })}
                                active={firstPageNumber + index === pageNumber}
                                onClick={() => onPageChange(firstPageNumber + index)}
                                data-metadata={JSON.stringify({ name: `Page ${firstPageNumber + index}` })}
                            >
                                {isActivePage(firstPageNumber + index) ? activePageTitle(firstPageNumber + index) : firstPageNumber + index}
                            </Pagination.Item>
                        ))}
                        {showPrevNext && (
                            <Pagination.Item
                                linkClassName="py-1 px-3"
                                aria-label={pageNumber === pagesCount ? undefined : `Goto Next Page, Page ${pageNumber + 1}`}
                                disabled={pageNumber === pagesCount}
                                onClick={() => onPageChange(pageNumber + 1)}
                                data-metadata={JSON.stringify({ name: `Goto Next Page, Page ${pageNumber + 1}` })}
                            >
                                <Icon icon="chevron-right" style={{ height: 13 }} />
                            </Pagination.Item>
                        )}
                    </Pagination>
                </nav>
                {!isMobile && (
                    <div className="d-flex gap-2 me-auto align-items-center">
                        <Form.Control
                            name="page"
                            aria-label="Jump To Page Input"
                            onKeyDown={(e) => !pageDoesntExist && e.key === "Enter" && jumpPage && onPageChange(jumpPage)}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                isNumber(value);
                                if (e.target.value === "") {
                                    setJumpPage(undefined);
                                } else if (!isNumber(value)) {
                                    setJumpPage(0);
                                } else {
                                    setJumpPage(Number(e.target.value));
                                }
                            }}
                            className={classNames("bg-white page-input", {
                                "is-invalid": pageDoesntExist,
                            })}
                            type="number"
                        />
                        <Button
                            title="Jump To Page"
                            disabled={isNil(jumpPage) || pageDoesntExist}
                            onClick={() => jumpPage && onPageChange(jumpPage)}
                            variant="secondary"
                        >
                            Go
                        </Button>
                    </div>
                )}
                {pageDoesntExist && <span className={classNames("text-danger me-auto")}>Page doesn't exist</span>}
            </div>
        </div>
    );
};
